import { useState, useEffect } from "react";
import { F, D } from "../../../styles/palette";
import { DC, Bdg } from "../../shared";
import usePhotoUpload from "../../../hooks/usePhotoUpload";
import PhotoUpload from "./PhotoUpload";

const REGION_COLORS = {
  "Miembro superior derecho": "#0284c7",
  "Miembro superior izquierdo": "#0369a1",
  "Miembro inferior derecho": "#7c3aed",
  "Miembro inferior izquierdo": "#6d28d9",
  "Mano derecha": "#0891b2",
  "Mano izquierda": "#0e7490",
  "Pie derecho": "#059669",
  "Pie izquierdo": "#047857",
  "Tronco / Tórax": "#d97706",
  "Abdomen / Pelvis": "#b45309",
  "Cabeza / Cuello": "#dc2626",
  "Genital": "#9f1239",
  "Otra región": "#64748b",
};

function formatDate(d) {
  if (!d) return "";
  const date = d.toDate ? d.toDate() : new Date(d);
  return date.toLocaleDateString("es-CO", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function ArchivoFotografico({ patientId, user, patient }) {
  const { photos, loading, uploading, uploadProgress, loadPhotos, uploadPhoto } = usePhotoUpload(patientId);
  const [view, setView] = useState("gallery");
  const [filterRegion, setFilterRegion] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [compareA, setCompareA] = useState(null);
  const [compareB, setCompareB] = useState(null);

  useEffect(() => { loadPhotos(filterRegion); }, [loadPhotos, filterRegion]);

  const regions = [...new Set(photos.map(p => p.regionCorporal))];

  if (view === "upload") {
    return (
      <PhotoUpload
        user={user}
        patientId={patientId}
        uploadPhoto={uploadPhoto}
        onClose={() => setView("gallery")}
        onUploaded={() => loadPhotos(filterRegion)}
      />
    );
  }

  const handlePhotoClick = (photo) => {
    if (compareMode) {
      if (!compareA) setCompareA(photo);
      else if (!compareB && photo.id !== compareA.id) setCompareB(photo);
      else { setCompareA(photo); setCompareB(null); }
    } else {
      setLightbox(photo);
    }
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={() => setView("upload")}
          style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#0284c7", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: F.b }}>
          + Subir Foto
        </button>
        <button onClick={() => { setCompareMode(!compareMode); setCompareA(null); setCompareB(null); }}
          style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${compareMode ? "#ca8a04" : D.bdr}`, background: compareMode ? "rgba(202,138,4,0.1)" : "transparent", color: compareMode ? "#fbbf24" : "#94a3b8", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: F.b }}>
          {compareMode ? "Salir comparación" : "Comparar"}
        </button>

        <div style={{ flex: 1 }} />

        <select
          value={filterRegion || ""}
          onChange={e => setFilterRegion(e.target.value || null)}
          style={{ padding: "7px 12px", borderRadius: 7, border: `1px solid ${D.bdr}`, background: "rgba(30,41,59,0.8)", color: "#e2e8f0", fontSize: 12, fontFamily: F.b }}>
          <option value="">Todas las regiones</option>
          {regions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>

        <span style={{ color: "#64748b", fontSize: 12, fontFamily: F.m }}>{photos.length} foto{photos.length !== 1 && "s"}</span>
      </div>

      {compareMode && (
        <p style={{ color: "#fbbf24", fontSize: 12, marginBottom: 10 }}>
          Seleccione dos fotos para comparar lado a lado. {compareA && !compareB && "Seleccione la segunda foto."}
        </p>
      )}

      {/* Compare view */}
      {compareMode && compareA && compareB && (
        <DC title="Comparación" accent="#ca8a04">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[compareA, compareB].map((p, i) => (
              <div key={i}>
                <img src={p.url} alt={p.regionCorporal}
                  style={{ width: "100%", borderRadius: 8, border: `1px solid ${D.bdr}`, aspectRatio: "4/3", objectFit: "cover" }} />
                <div style={{ marginTop: 6 }}>
                  <Bdg t={p.regionCorporal} c={REGION_COLORS[p.regionCorporal] || "#64748b"} />
                  <p style={{ color: "#94a3b8", fontSize: 11, margin: "4px 0 0" }}>{formatDate(p.createdAt)}</p>
                  {p.descripcion && <p style={{ color: "#64748b", fontSize: 11, margin: "2px 0 0" }}>{p.descripcion}</p>}
                </div>
              </div>
            ))}
          </div>
        </DC>
      )}

      {/* Loading */}
      {loading && <p style={{ color: D.txM, fontSize: 13, textAlign: "center", padding: 20 }}>Cargando fotos...</p>}

      {/* Empty state */}
      {!loading && photos.length === 0 && (
        <DC>
          <div style={{ textAlign: "center", padding: 30 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>&#128247;</div>
            <p style={{ color: D.txM, fontSize: 14, margin: "0 0 8px" }}>Sin fotografías clínicas</p>
            <p style={{ color: "#475569", fontSize: 12, margin: "0 0 16px" }}>Suba la primera foto para documentar la evolución del paciente.</p>
            <button onClick={() => setView("upload")}
              style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#0284c7", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: F.b }}>
              + Subir primera foto
            </button>
          </div>
        </DC>
      )}

      {/* Photo grid */}
      {!loading && photos.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10 }}>
          {photos.map(p => {
            const isSelectedA = compareMode && compareA?.id === p.id;
            const isSelectedB = compareMode && compareB?.id === p.id;
            return (
              <div key={p.id} onClick={() => handlePhotoClick(p)}
                style={{
                  borderRadius: 10, overflow: "hidden", cursor: "pointer",
                  border: `2px solid ${isSelectedA ? "#fbbf24" : isSelectedB ? "#f97316" : D.bdr}`,
                  background: D.card, transition: "border-color 0.2s",
                }}
                onMouseEnter={e => { if (!compareMode) e.currentTarget.style.borderColor = "rgba(2,132,199,0.5)"; }}
                onMouseLeave={e => { if (!compareMode) e.currentTarget.style.borderColor = D.bdr; }}>
                <img src={p.url} alt={p.regionCorporal}
                  style={{ width: "100%", aspectRatio: "4/3", objectFit: "cover", display: "block" }} />
                <div style={{ padding: "8px 10px" }}>
                  <div style={{ marginBottom: 4 }}>
                    <Bdg t={p.regionCorporal} c={REGION_COLORS[p.regionCorporal] || "#64748b"} />
                  </div>
                  <p style={{ color: "#94a3b8", fontSize: 10, margin: 0, fontFamily: F.m }}>{formatDate(p.createdAt)}</p>
                  {p.descripcion && <p style={{ color: "#64748b", fontSize: 10, margin: "2px 0 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.descripcion}</p>}
                  {(isSelectedA || isSelectedB) && (
                    <span style={{ fontSize: 10, color: "#fbbf24", fontWeight: 700, fontFamily: F.m }}>{isSelectedA ? "A" : "B"}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, cursor: "pointer" }}>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: 800, width: "100%", cursor: "default" }}>
            <img src={lightbox.url} alt={lightbox.regionCorporal}
              style={{ width: "100%", borderRadius: 10, maxHeight: "70vh", objectFit: "contain" }} />
            <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <Bdg t={lightbox.regionCorporal} c={REGION_COLORS[lightbox.regionCorporal] || "#64748b"} />
                <p style={{ color: "#94a3b8", fontSize: 12, margin: "6px 0 0" }}>{formatDate(lightbox.createdAt)}</p>
                {lightbox.descripcion && <p style={{ color: "#cbd5e1", fontSize: 13, margin: "4px 0 0" }}>{lightbox.descripcion}</p>}
                <p style={{ color: "#475569", fontSize: 11, margin: "6px 0 0", fontFamily: F.m }}>
                  Registrado por: {lightbox.doctorNombre} &middot; {lightbox.sizeBytes ? `${(lightbox.sizeBytes / 1024).toFixed(0)} KB` : ""}
                </p>
              </div>
              <button onClick={() => setLightbox(null)}
                style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid rgba(255,255,255,0.2)`, background: "transparent", color: "#fff", fontSize: 12, cursor: "pointer", fontFamily: F.b }}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
